import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Alert, Button, Checkbox, Drawer, Input, Select, Space, Spin, Tag, Typography,
} from 'antd';

import {
  previewCompanyWhatsAppTemplate,
  updateCompanyWhatsAppTemplate,
} from '../api';
import { getCompanyContactsV3 } from '../../../reducers/contact/resources';
import { apiError, unwrap } from './shared';
import {
  BUTTON_PAYLOAD_MAX, DraftSlot, KIND_OPTIONS, MANUAL_ONLY, MergeFields, SenderTemplate, TemplateButton,
  buildSourceGroups, buttonsAreValid, buttonsForSave, canAsk, decodeChoice, draftFromParams, draftIsValid,
  kindWarning, needsFallback, normalizeButtons, normalizeTemplate, paramsFromDraft, splitTemplateBody,
  validateButtonPayload, validateDraft,
} from './templateMapping';

const { Text } = Typography;

const SLOT_COLORS = ['#1677ff', '#13a8a8', '#722ed1', '#d46b08', '#c41d7f', '#389e0d', '#0958d9', '#cf1322'];
const slotColor = (n: number): string => SLOT_COLORS[(n - 1) % SLOT_COLORS.length];

/** `{{n}}` as a coloured chip; matches the colour of the placeholder's row below. */
export const SlotChip: React.FC<{ index: number }> = ({ index }) => (
  <span
    data-testid={`template-chip-${index}`}
    style={{
      display: 'inline-block',
      padding: '0 6px',
      margin: '0 1px',
      borderRadius: 4,
      fontSize: 12,
      lineHeight: '18px',
      fontWeight: 600,
      color: '#fff',
      background: slotColor(index),
    }}
  >
    {`{{${index}}}`}
  </span>
);

export const TemplateBody: React.FC<{ body: string }> = ({ body }) => (
  <div style={{ whiteSpace: 'pre-wrap', lineHeight: '22px' }} data-testid="template-mapping-body">
    {splitTemplateBody(body).map((s, i) => (
      s.type === 'slot' ? <SlotChip key={`s${i}`} index={s.index} /> : <span key={`t${i}`}>{s.text}</span>
    ))}
  </div>
);

interface ContactOption { value: string; label: string }

interface Props {
  open: boolean;
  companyId: string;
  template: SenderTemplate | null;
  mergeFields: MergeFields | null;
  onClose(): void;
  onSaved(t: SenderTemplate): void;
}

/** Map a synced template's `{{n}}` placeholders and its automatic use (ADR-037 phase 0A). */
const TemplateMappingDrawer: React.FC<Props> = ({
  open, companyId, template, mergeFields, onClose, onSaved,
}) => {
  const groups = useMemo(() => buildSourceGroups(mergeFields), [mergeFields]);
  const count = template ? Math.max(template.placeholderCount || 0, 0) : 0;

  const [slots, setSlots] = useState<DraftSlot[]>([]);
  const [buttons, setButtons] = useState<TemplateButton[]>([]);
  const [kind, setKind] = useState<string>(MANUAL_ONLY);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [contactId, setContactId] = useState<string | undefined>(undefined);
  const [contactOptions, setContactOptions] = useState<ContactOption[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [preview, setPreview] = useState<string>('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const searchSeq = useRef(0);

  useEffect(() => {
    if (!open || !template) return;
    setSlots(draftFromParams(template.params, count));
    setButtons(normalizeButtons(template.buttons));
    setKind(template.kind || MANUAL_ONLY);
    setTouched(false);
    setSaveError(null);
    setPreview('');
    setPreviewError(null);
  }, [open, template, count]);

  const searchContacts = async (search: string) => {
    if (!companyId) return;
    searchSeq.current += 1;
    const seq = searchSeq.current;
    setContactsLoading(true);
    try {
      const res = unwrap(await getCompanyContactsV3(companyId, {
        search, page: 1, pageSize: 20, sort: 'created_at', order: 'desc',
      })) || {};
      if (seq !== searchSeq.current) return;
      const rows: any[] = Array.isArray(res.rows) ? res.rows : [];
      setContactOptions(rows.map((r) => ({
        value: r.id,
        label: [r.name || 'Unnamed contact', r.mobile].filter(Boolean).join(' · '),
      })));
    } catch {
      if (seq === searchSeq.current) setContactOptions([]);
    } finally {
      if (seq === searchSeq.current) setContactsLoading(false);
    }
  };

  useEffect(() => {
    if (open) searchContacts('');
  }, [open, companyId]);

  const errors = validateDraft(slots, kind);
  const valid = draftIsValid(slots, kind) && buttonsAreValid(buttons);
  const params = useMemo(() => paramsFromDraft(slots, groups), [slots, groups]);
  /** Params the recruiter is asked for, in order (a send's `inputs[i]` answers the i-th). */
  const asked = params.filter((p) => p.source === 'input' || (p.ask && canAsk(String(p.source))));
  const inputLabels = asked.map((p) => String(p.label || ''));

  // Live preview: debounce, render the unsaved mapping server-side.
  useEffect(() => {
    if (!open || !template) return undefined;
    const ready = slots.every((s) => !!s.choice);
    if (!ready) {
      setPreview('');
      setPreviewError(null);
      return undefined;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const res = unwrap(await previewCompanyWhatsAppTemplate(companyId, template.id, {
          ...(contactId ? { contactId } : {}),
          params,
          // Sample answers for "ask when sending"; asked fields stay empty so the field fills them.
          inputs: asked.map((p) => (p.source === 'input' ? `[${p.label}]` : '')),
        })) || {};
        if (!cancelled) {
          setPreview(String(res.text || ''));
          setPreviewError(null);
        }
      } catch (err) {
        if (!cancelled) setPreviewError(apiError(err, 'Preview unavailable.'));
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, template, companyId, contactId, JSON.stringify(params)]);

  const updateSlot = (i: number, patch: Partial<DraftSlot>) => {
    setSlots((prev) => prev.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  };

  const updateButton = (index: number, payload: string) => {
    setButtons((prev) => prev.map((b) => (b.index === index ? { ...b, payload } : b)));
  };

  const handleSave = async () => {
    setTouched(true);
    if (!template || !valid) return;
    setSaving(true);
    setSaveError(null);
    try {
      const savedButtons = buttonsForSave(buttons);
      const saved = unwrap(await updateCompanyWhatsAppTemplate(companyId, template.id, {
        kind: kind === MANUAL_ONLY ? null : kind,
        params,
        ...(savedButtons.length ? { buttons: savedButtons } : {}),
      }));
      onSaved(saved && saved.id
        ? normalizeTemplate(saved)
        : {
          ...template, kind: kind === MANUAL_ONLY ? null : kind as any, params, buttons: savedButtons,
        });
    } catch (err: any) {
      const status = err && (err.statusCode || (err.response && err.response.status));
      if (status === 409) {
        const label = (KIND_OPTIONS.find((o) => o.value === kind) || { label: kind }).label;
        setSaveError(apiError(err, `Another template on this number is already used for “${label}”.`));
      } else {
        setSaveError(apiError(err, 'Failed to save the mapping.'));
      }
    } finally {
      setSaving(false);
    }
  };

  const kindOpt = KIND_OPTIONS.find((o) => o.value === kind);
  const warning = kindWarning(kind, slots);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={640}
      destroyOnClose
      title={template ? `Map fields · ${template.name}` : 'Map fields'}
      extra={(
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={handleSave} data-testid="template-mapping-save">
            Save
          </Button>
        </Space>
      )}
    >
      {template && (
        <div data-testid="template-mapping-drawer" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
              {[template.language, template.category].filter(Boolean).join(' · ')}
            </Text>
            <div style={{
              padding: '10px 12px', background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 6,
            }}
            >
              <TemplateBody body={template.body} />
            </div>
          </div>

          {count === 0 && (
            <Text type="secondary">This template has no placeholders. It is sent exactly as written.</Text>
          )}

          {slots.map((s, i) => {
            const n = i + 1;
            const { source } = decodeChoice(s.choice || '');
            const err = touched ? errors[i] : null;
            return (
              <div key={n} data-testid={`template-slot-${n}`} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ paddingTop: 5, width: 44 }}><SlotChip index={n} /></div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <Select
                    data-testid={`template-slot-source-${n}`}
                    placeholder="Fill with…"
                    value={s.choice}
                    showSearch
                    optionFilterProp="label"
                    options={groups.map((g) => ({ label: g.label, options: g.options }))}
                    onChange={(v: string) => updateSlot(i, { choice: v })}
                    status={err && !s.choice ? 'error' : undefined}
                    style={{ width: '100%' }}
                  />
                  {source === 'input' && (
                    <Input
                      data-testid={`template-slot-label-${n}`}
                      placeholder="Label the recruiter sees, e.g. Position"
                      value={s.extra}
                      maxLength={60}
                      onChange={(e) => updateSlot(i, { extra: e.target.value })}
                    />
                  )}
                  {source === 'static' && (
                    <Input
                      data-testid={`template-slot-value-${n}`}
                      placeholder="Fixed text"
                      value={s.extra}
                      maxLength={200}
                      onChange={(e) => updateSlot(i, { extra: e.target.value })}
                    />
                  )}
                  {canAsk(source) && (
                    <Checkbox
                      data-testid={`template-slot-ask-${n}`}
                      checked={!!s.ask}
                      onChange={(e) => updateSlot(i, { ask: e.target.checked })}
                    >
                      <span style={{ fontSize: 13 }}>Also ask when sending (a typed value wins)</span>
                    </Checkbox>
                  )}
                  {canAsk(source) && s.ask && (
                    <Input
                      data-testid={`template-slot-label-${n}`}
                      placeholder="Label the recruiter sees, e.g. Position"
                      value={s.extra}
                      maxLength={60}
                      onChange={(e) => updateSlot(i, { extra: e.target.value })}
                    />
                  )}
                  {s.choice && source !== 'static' && (
                    <Input
                      data-testid={`template-slot-fallback-${n}`}
                      addonBefore="Fallback"
                      placeholder={needsFallback(source) ? 'Used when the field is empty (required)' : 'Used when empty (optional)'}
                      value={s.fallback}
                      maxLength={200}
                      status={err && needsFallback(source) && !s.fallback.trim() ? 'error' : undefined}
                      onChange={(e) => updateSlot(i, { fallback: e.target.value })}
                    />
                  )}
                  {err && <Text type="danger" style={{ fontSize: 12 }}>{err}</Text>}
                </div>
              </div>
            );
          })}

          {buttons.length > 0 && (
            <div data-testid="template-mapping-buttons">
              <div style={{ fontWeight: 500, marginBottom: 2 }}>Buttons</div>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                What GoHire receives when the candidate taps this button. Match what your bot flow expects, e.g. 1 for Confirm.
              </Text>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {buttons.map((b) => {
                  const err = touched ? validateButtonPayload(b.payload) : null;
                  return (
                    <div key={b.index} data-testid={`template-button-${b.index}`} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                      <div style={{ paddingTop: 4, width: 140, flexShrink: 0 }}>
                        <Tag style={{ marginInlineEnd: 0, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.text}</Tag>
                      </div>
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <Input
                          data-testid={`template-button-payload-${b.index}`}
                          addonBefore="Reply value"
                          placeholder={b.text}
                          value={b.payload}
                          maxLength={BUTTON_PAYLOAD_MAX}
                          status={err ? 'error' : undefined}
                          onChange={(e) => updateButton(b.index, e.target.value)}
                        />
                        {err && <Text type="danger" style={{ fontSize: 12 }}>{err}</Text>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>Automatic use</div>
            <Select
              data-testid="template-mapping-kind"
              value={kind}
              onChange={(v: string) => setKind(v)}
              options={KIND_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
              style={{ width: '100%' }}
            />
            {kindOpt && (
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }} data-testid="template-mapping-kind-help">
                {kindOpt.help}
              </Text>
            )}
            {warning && (
              <Alert
                type="warning"
                showIcon
                style={{ marginTop: 8 }}
                message={warning}
                data-testid="template-mapping-kind-warning"
              />
            )}
          </div>

          <div>
            <div style={{
              display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 4,
            }}
            >
              <span style={{ fontWeight: 500 }}>Preview</span>
              {previewLoading && <Spin size="small" />}
            </div>
            <Select
              data-testid="template-mapping-contact"
              allowClear
              showSearch
              filterOption={false}
              placeholder="Sample data — or pick a contact"
              value={contactId}
              loading={contactsLoading}
              options={contactOptions}
              onSearch={(v) => searchContacts(v)}
              onChange={(v?: string) => setContactId(v)}
              notFoundContent={contactsLoading ? <Spin size="small" /> : 'No contacts'}
              style={{ width: '100%', marginBottom: 8 }}
            />
            <div
              style={{
                background: '#e5ddd5', borderRadius: 8, padding: '12px 12px 12px 48px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end',
              }}
            >
              <div
                data-testid="template-mapping-preview"
                style={{
                  background: '#dcf8c6',
                  color: '#111b21',
                  borderRadius: '8px 0 8px 8px',
                  padding: '6px 10px',
                  maxWidth: '100%',
                  fontSize: 14,
                  lineHeight: '19px',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  minHeight: 32,
                }}
              >
                {preview || (
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    {slots.every((s) => !!s.choice) ? '…' : 'Choose a source for every placeholder to see the preview.'}
                  </Text>
                )}
              </div>
              {buttons.length > 0 && (
                <div
                  data-testid="template-mapping-preview-buttons"
                  style={{
                    display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4, alignSelf: 'stretch',
                  }}
                >
                  {buttons.map((b) => (
                    <div
                      key={b.index}
                      title={`Sends “${b.payload.trim() || b.text}”`}
                      style={{
                        background: '#fff',
                        color: '#027eb5',
                        borderRadius: 8,
                        padding: '6px 10px',
                        textAlign: 'center',
                        fontSize: 14,
                        lineHeight: '19px',
                      }}
                    >
                      {b.text}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {previewError && <Text type="danger" style={{ fontSize: 12 }}>{previewError}</Text>}
            {inputLabels.length > 0 && (
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                {'Asked when sending: '}
                {inputLabels.map((l) => <Tag key={l} style={{ marginInlineEnd: 4 }}>{l}</Tag>)}
              </Text>
            )}
          </div>

          {saveError && <Alert type="error" showIcon message={saveError} data-testid="template-mapping-error" />}
        </div>
      )}
    </Drawer>
  );
};

export default TemplateMappingDrawer;
